import {
  AssignCustomersRequestBody,
  AssignCustomersResponse,
  CreateSalespersonListFormType,
  CreateSalespersonListResponse,
  DeleteSalespersonListRequestBody,
  DeleteSalespersonListResponse,
  EditSalespersonListFormType,
  EditSalespersonListResponse,
  SalespersonActiveCustomerResponse,
  SalespersonActiveCustomersRequestBody,
  SalespersonListDetailRequestBody,
  SalespersonListDetailResponse,
  SalespersonListParams,
  SalespersonListResponse,
  SalespersonTransactionListRequestBody,
  SalespersonTransactionListResponse,
  TerminateCustomerRequestBody,
  TerminateCustomerResponse,
  ToggleSalespersonStatusRequestBody,
  ToggleSalespersonStatusResponse,
} from "src/pages/UserManagement/SalespersonList/api/interfaces";
import {
  getSalespersonListSortingParams,
  SalespersonListSortValueType,
} from "src/pages/UserManagement/SalespersonList/hooks/useSalespersonListHandler";
import { getPagination } from "src/utils/getPagination";
import API from ".";
import { format } from "date-fns";
import { convertArrayParamsToString } from "src/utils/convertArrayParamsToString";
import {
  getSalespersonDetailTransactionListSortingParams,
  SalespersonDetailTransactionListSortValueType,
} from "src/pages/UserManagement/SalespersonList/hooks/useSalespersonDetailTransactionListHandler";

const featureRouteApi = "/salespeople";

const convertCustomers = ({
  form,
  isEdit,
}: {
  form:
    | CreateSalespersonListFormType
    | EditSalespersonListFormType
    | AssignCustomersRequestBody;
  isEdit?: boolean;
}) => {
  return form.customers.map((customer) => ({
    ...(isEdit && { id: customer.id ?? null }),
    customer_id: customer.customerId,
    contract_start_date: customer.contractDatePeriod?.from
      ? format(customer.contractDatePeriod?.from, "yyyy-MM-dd")
      : null,
    contract_end_date: customer.contractDatePeriod?.to
      ? format(customer.contractDatePeriod?.to, "yyyy-MM-dd")
      : null,
  }));
};

const convertFormData = ({
  form,
  isEdit,
}: {
  form: CreateSalespersonListFormType | EditSalespersonListFormType;
  isEdit?: boolean;
}) => {
  return {
    name: form.name,
    phone_number: form.phoneNumber,
    country: form.country,
    city: form.city,
    customers: convertCustomers({ form, isEdit }),
  };
};

export const getSalespersonList = async ({
  limit,
  offset,
  salespersonListNameOrPhoneNumber,
  is_active,
  sortBy,
  country,
  city,
}: SalespersonListParams): Promise<SalespersonListResponse> => {
  const paginationParam = getPagination({ limit, offset });

  const response = await API.get<SalespersonListResponse>(featureRouteApi, {
    params: {
      ...paginationParam,
      ...(salespersonListNameOrPhoneNumber
        ? { qn: salespersonListNameOrPhoneNumber }
        : {}),
      ...(sortBy
        ? getSalespersonListSortingParams(
            sortBy as SalespersonListSortValueType
          )
        : {}),
      ...(country.length > 0
        ? {
            countries: convertArrayParamsToString(
              country.map((option) => option.value)
            ),
          }
        : {}),
      ...(city.length > 0
        ? {
            cities: convertArrayParamsToString(
              city.map((option) => option.value)
            ),
          }
        : {}),
      ...(is_active ? { is_active } : {}),
    },
  });

  return response.data;
};

export const getSalespersonListDetail = async ({
  id,
}: SalespersonListDetailRequestBody): Promise<SalespersonListDetailResponse> => {
  const response = await API.get<SalespersonListDetailResponse>(
    `${featureRouteApi}/${id}`
  );

  return response.data;
};

export const getSalespersonActiveCustomers = async ({
  limit,
  offset,
  id,
  status,
  contractStartDate,
  contractEndDate,
}: SalespersonActiveCustomersRequestBody): Promise<SalespersonActiveCustomerResponse> => {
  const paginationParam = getPagination({ limit, offset });

  const response = await API.get<SalespersonActiveCustomerResponse>(
    `${featureRouteApi}/${id}/customers`,
    {
      params: {
        ...paginationParam,
        status,
        ...(contractStartDate
          ? { contract_start_date: format(contractStartDate, "yyyy-MM-dd") }
          : {}),
        ...(contractEndDate
          ? { contract_end_date: format(contractEndDate, "yyyy-MM-dd") }
          : {}),
      },
    }
  );

  return response.data;
};

export const getSalespersonTransactionList = async ({
  limit,
  offset,
  id,
  sortBy,
  qn,
  startOrderDate,
  endOrderDate,
  businessOperation,
}: SalespersonTransactionListRequestBody): Promise<SalespersonTransactionListResponse> => {
  const paginationParam = getPagination({ limit, offset });

  const response = await API.get<SalespersonTransactionListResponse>(
    `${featureRouteApi}/${id}/transactions`,
    {
      params: {
        ...paginationParam,
        ...(sortBy
          ? getSalespersonDetailTransactionListSortingParams(
              sortBy as SalespersonDetailTransactionListSortValueType
            )
          : {}),
        ...(qn ? { qn } : {}),
        ...(startOrderDate
          ? { start_order_date: format(startOrderDate, "yyyy-MM-dd") }
          : {}),
        ...(endOrderDate
          ? { end_order_date: format(endOrderDate, "yyyy-MM-dd") }
          : {}),
        ...(businessOperation.length > 0
          ? {
              business_operation_ids: convertArrayParamsToString(
                businessOperation.map((option) => option.value)
              ),
            }
          : {}),
      },
    }
  );

  return response.data;
};

export const createSalespersonList = async (
  form: CreateSalespersonListFormType
): Promise<CreateSalespersonListResponse> => {
  const requestBody = convertFormData({ form });

  const response = await API.post<CreateSalespersonListResponse>(
    featureRouteApi,
    requestBody
  );

  return response.data;
};

export const editSalespersonList = async ({
  id,
  ...form
}: EditSalespersonListFormType): Promise<EditSalespersonListResponse> => {
  const requestBody = convertFormData({ form, isEdit: true });

  const response = await API.put<EditSalespersonListResponse>(
    `${featureRouteApi}/${id}`,
    requestBody
  );

  return response.data;
};

export const deleteSalespersonList = async ({
  id,
}: DeleteSalespersonListRequestBody): Promise<DeleteSalespersonListResponse> => {
  const response = await API.delete<DeleteSalespersonListResponse>(
    `${featureRouteApi}/${id}`
  );

  return response.data;
};

export const toggleSalespersonStatus = async ({
  id,
}: ToggleSalespersonStatusRequestBody): Promise<ToggleSalespersonStatusResponse> => {
  const response = await API.patch<ToggleSalespersonStatusResponse>(
    `${featureRouteApi}/${id}/status`
  );

  return response.data;
};

export const assignCustomers = async (
  form: AssignCustomersRequestBody
): Promise<AssignCustomersResponse> => {
  const requestBody = { customers: convertCustomers({ form, isEdit: false }) };

  const response = await API.post<AssignCustomersResponse>(
    `${featureRouteApi}/${form.id}/customers`,
    requestBody
  );

  return response.data;
};

export const terminateCustomer = async ({
  id,
  customerId,
}: TerminateCustomerRequestBody): Promise<TerminateCustomerResponse> => {
  const response = await API.delete<TerminateCustomerResponse>(
    `${featureRouteApi}/${id}/customers/${customerId}`
  );

  return response.data;
};
